// Copyright (c) 2017+,COMUNICARE SOLUTIONS SA
// All rights reserved.
//
// Redistribution and use in source and binary forms, with or without modification,
// are NOT permitted.

import * as moment from "moment";
import { Tools } from "../helpers/tools-helper";
import { IDrugInfo, IPackExtended } from "./drugsInfo";
import { IKeyValue } from "./keyValue";
import { IDeviceNotification, NOTIFICATION_STATUS } from "./notification";
import { ACTION_STATUS_ENTITY, ACTIVITY_LOCATION, IEntity, Period, Reference, STATUS_ENTITY, Timing } from "./sharedInterfaces";

/**
 *  type of object data handled by link
 */
export enum ENTITY_TYPE {
  NOTE = 1,
  PHOTO = 2,
  DRUG = 3,
  QUESTIONNAIRE = 4,
}

/**
 * Parent type for Entity
 */
export enum PARENT_TYPE {
  CAREPLAN = 1,
  ACTIVITY = 2,
  ACTIVITY_DRUG = 3,
  DRUG = 4,
  APPOINTEMENT = 5,
  PATIENT = 6,
}

/**
 *  Interfaces used to handle any kind of custom data linked to another entity
 */
export interface IEntitylink extends IEntity {
  entityType: ENTITY_TYPE;
  caremateOwnerId: string; // caremateIdentifier for owner
  parentType: PARENT_TYPE; // parent type
  parentId: string; // parent identifier
  entityData: any; // may contains EntityNote, EntityDrug or EntityQuestionnaire interface
}

/**
 * Interface for scheduled drug
 */
export interface DrugScheduled extends Period {
  name: string;
  prescriptionName?: string;
  description: string;
  status?: NOTIFICATION_STATUS; // notification status to display
  notificationComment?: string; // comment from notification
  drugId: string;
  beforeEditingStatus?: NOTIFICATION_STATUS;
  beforeEditingComment?: string;
  beforeEditingQuantity?: string;
  device?: IDeviceNotification;
  reference: string;
  unscheduledIntake?: boolean;
  author?: Reference;
  moment?: string; // rising, morning, noon, evening, beding
  quantity?: string;
  quantityTaken: string;
}

/**
 * Interface to link Drug extra data to Careplan  (from hospital, unmodifiable) or Patient (personal drug)
 */
export class EntityDrug {
  public static get PERFORMER_HOSPITAL(): string {
    return "H";
  }
  public static get PERFORMER_FAMILYDOCTOR(): string {
    return "D";
  }
  public static get PERFORMER_PHARMACIST(): string {
    return "P";
  }

  public static get QUANTITTY(): IKeyValue[] {
    return [
      {
        key: "1/4",
        value: 0.25,
      },
      {
        key: "1/3",
        value: 0.33,
      },
      {
        key: "1/2",
        value: 0.5,
      },
      {
        key: "2/3",
        value: 0.66,
      },
      {
        key: "3/4",
        value: 0.75,
      },
    ];
  }

  public notify: boolean;
  public name: string;
  public prescriptionName?: string;
  public reference: string;
  public quantity: string;
  public frequency: Timing;
  public location: ACTIVITY_LOCATION;
  public performer: Reference;
  // public timingCode: string;    // Now in Frequency (Timing)
  public comment: string; // only modifiable in both cases
  public photo: string; // photo data/url
  public cycle?: CycleSchema;
  public source?: string; // country/dbName : BE/samv2, FR/bdpm
  public stepwiseSchema?: IStepwiseDrug;
  public atcCode?: string;
  public stock?: IDrugStock;
  public knownPackaging?: boolean;
  public managedStock?: boolean;
  public allPack?: IPackExtended[];
  public lastPackNumber?: number;
  public vmpCode?: string;

  /**
   *
   * @param quantityDisplay string with number and fraction (ex: 1 + 1/2)
   * @returns a string representinf a number (ex: 1.5)
   */
  public static computeQuantityValue(quantityDisplay: string, mustBeNumber: boolean): string {
    if (!quantityDisplay || quantityDisplay === "0") {
      return null;
    }
    const split = quantityDisplay.split(" + ");
    const lessThan0 = split[0].includes("/");
    if (split.length === 1 && !lessThan0) {
      if (isNaN(Number(quantityDisplay)) && mustBeNumber) return "1";
      return quantityDisplay;
    }
    const n = lessThan0 ? 0 : isNaN(Number(split[0])) ? 1 : Number(split[0]);
    const div = lessThan0 ? split[0].split("/") : split[1].split("/");
    if (div.length !== 2) {
      return n.toString();
    }
    const d1 = isNaN(Number(div[0])) ? 0 : Number(div[0]);
    const d2 = isNaN(Number(div[1])) ? 1 : Number(div[1]);
    const val = d1 / d2;
    const roundedDiv = Math.floor(val * 100) / 100;
    const quantity = n + roundedDiv;
    return quantity.toString();
  }

  /**
   *
   * @param quantityValue a string representinf a number (ex: 1.5)
   * @returns string with number and fraction (ex: 1 + 1/2)
   */
  public static computeQuantityDisplay(quantityValue: string): string {
    if (!quantityValue) return null;
    if (isNaN(Number(quantityValue))) return quantityValue;
    const val = Number(quantityValue);
    const frac = Math.floor((val % 1) * 100) / 100;
    const num = val - frac;
    const allQuantities = EntityDrug.QUANTITTY;
    const fracString = allQuantities.find((i) => i.value === frac) ?? "";
    if (fracString) {
      return (num > 0 ? num + " + " : "") + fracString.key;
    }
    return quantityValue;
  }

  public static getQuantitySurplus(quantity: string, quantityTaken: string): number {
    if (!Number.isFinite(Number(quantity)) || !Number.isFinite(Number(quantity))) {
      return 0;
    }
    const surplus = Number(quantityTaken) - Number(quantity);
    return surplus > 0 ? surplus : 0;
  }

  public static getTotalQuantityForDay(drug: EntityDrug): number {
    const moments = ["rise", "morning", "noon", "evening", "bedtime"];
    return this.getTotalQuantityForMoments(drug, moments);
  }
  public static getTotalQuantityForMoments(drug: EntityDrug, moments: string[]): number {
    let sum = 0;
    for (const m of moments) {
      const q = drug.frequency?.quantities?.[m];
      if (q && Number.isFinite(Number(q))) {
        sum += Number(q);
      }
    }
    const q = drug.quantity && Number.isFinite(Number(drug.quantity)) ? Number(drug.quantity) : 0;
    return sum > 0 ? sum : q > 0 ? q : 1;
  }

  public static numberDosesNeeded(drug: EntityDrug, nbDays: number): number {
    const dosesPerDay = this.getTotalQuantityForDay(drug);
    return nbDays * dosesPerDay;
  }

  public static numberDaysRemaining(drug: EntityDrug, nbDosesRemaining: number): number {
    if (!nbDosesRemaining) return 0;
    const dosesPerDay = this.getTotalQuantityForDay(drug);
    const days = nbDosesRemaining / dosesPerDay;
    const roundedDecimalDays = Math.floor(Number(days) * 10) / 10;
    return roundedDecimalDays;
  }
}

export interface IDrugStock {
  totalUsages: number;
  totalRemainingUsages: number;
  totalRemainingDays: number;
}

export interface CycleSchema {
  name: string;
  cycle: boolean[];
  startDate: Date;
  pauseDate?: Date[];
}

export interface IStepwise {
  startDay: number;
  days: {
    moment: {
      timingCode: string; // R=rise, M=morning, N=noon, E=evening, B=bedtime
      drugs: {
        name: string;
        prescriptionName?: string;
        quantity: string;
        drugInfo?: IDrugInfo;
        source?: string; // be/samv2 ou fr/bdpm
      }[];
    }[];
  }[];
}

export interface IStepwiseDrug {
  name: string;
  stepwises: IStepwise[];
}

/**
 * interface to link a note
 */
export interface EntityNote {
  name: string;
  datetime: string;
  data: string;
  // isPrivate: boolean;
  photo: string;
}

/**
 *  interface to link a questionnaire
 * a questionnaire can be link to careplan or patient
 */
export interface EntityQuestionnaire {
  identifier: string;
  version: string;
  frequency: Timing;
}

/**
 * Helper class on IEntitylink interface
 */
export class Entitylink {
  /**
   * create a empty note
   */
  public static createNote(ownerId: string, parentId: string, parentType: PARENT_TYPE): IEntitylink {
    return {
      _id: Tools.genValidId(),
      entityType: ENTITY_TYPE.NOTE,
      caremateOwnerId: ownerId,
      creation: moment().format(),
      modified: moment().format(),
      actionStatus: ACTION_STATUS_ENTITY.CREATED,
      entityStatus: [STATUS_ENTITY.ACTIVE, STATUS_ENTITY.SHARED],
      parentId: parentId,
      parentType: parentType,
      entityData: {
        name: "",
        data: "",
        photo: "",
        // "isPrivate": true
      },
    };
  }

  /**
   * create a empty drug
   */
  public static createDrug(ownerId: string, parentId: string, parentType: PARENT_TYPE): IEntitylink {
    // create main Entitylink
    const entity: IEntitylink = {
      _id: Tools.genValidId(),
      entityType: ENTITY_TYPE.DRUG,
      caremateOwnerId: ownerId,
      creation: moment().format(),
      modified: moment().format(),
      actionStatus: ACTION_STATUS_ENTITY.CREATED,
      entityStatus: [STATUS_ENTITY.ACTIVE],
      parentId: parentId,
      parentType: parentType,
      entityData: {},
    };
    // create specific EntityDrug
    const drug: EntityDrug = {
      notify: true,
      name: "",
      reference: "",
      quantity: "",
      frequency: {
        boundsPeriod: {
          start: moment().format(),
          end: moment().add(1, "months").format(),
        },
        count: null,
        frequency: 1,
        period: 1,
        periodUnits: "d",
        when: "",
        timingCode: undefined, // R=rise, M=morning, N=noon, E=evening, B=bedtime,
        quantities: {
          rise: null,
          morning: null,
          noon: null,
          evening: null,
          bedtime: null,
        },
      },
      location: 0,
      performer: {
        reference: EntityDrug.PERFORMER_HOSPITAL,
        display: "hospital",
      },
      comment: "", // only modifiable in both cases
      photo: null,
      source: null,
      atcCode: null,
      knownPackaging: false,
      managedStock: false,
      lastPackNumber: -1,
      vmpCode: null,
    };
    entity.entityData = drug;
    return entity;
  }
}
